import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import {
  nowStartOfDayInZone,
  toDateTimeInZone,
  toStartOfDayInZone,
} from "./slotPickerDateTime";
import { type SiteSurveySlot } from "checkout/api/siteSurvey";

const getTodayStart = (minDate?: DateTime, timeZoneId?: string): DateTime => {
  if (minDate?.isValid) {
    return toStartOfDayInZone(minDate, timeZoneId);
  }
  return nowStartOfDayInZone(timeZoneId);
};

const useSiteSurveySlotPickerState = (
  slots: SiteSurveySlot[] | undefined,
  columns: number,
  minDate?: DateTime,
  pickerResetKey?: string | number,
  timeZoneId?: string
) => {
  const minSchedulableDate = useMemo(
    () => toStartOfDayInZone(minDate ?? DateTime.now(), timeZoneId),
    [minDate, timeZoneId]
  );

  const todayStart = useMemo(
    () => getTodayStart(minDate, timeZoneId),
    [minDate, timeZoneId]
  );

  const initialStartDate = useMemo(
    () => DateTime.max(todayStart, minSchedulableDate),
    [todayStart, minSchedulableDate]
  );

  const [startDateOverwritten, setStartDateOverwritten] = useState(false);
  const [startDate, setStartDate] = useState<DateTime>(initialStartDate);
  const [selectedSlot, setSelectedSlot] = useState<SiteSurveySlot>();

  const incrementDate = useCallback(() => {
    setStartDate((d) =>
      DateTime.max(
        toStartOfDayInZone(d, timeZoneId).plus({ day: columns }),
        minSchedulableDate
      )
    );
  }, [columns, minSchedulableDate, timeZoneId]);

  const decrementDate = useCallback(() => {
    setStartDate((d) =>
      DateTime.max(
        toStartOfDayInZone(d, timeZoneId).plus({ day: -columns }),
        minSchedulableDate
      )
    );
  }, [columns, minSchedulableDate, timeZoneId]);

  useEffect(() => {
    setStartDate((d) =>
      DateTime.max(toStartOfDayInZone(d, timeZoneId), minSchedulableDate)
    );
  }, [minSchedulableDate, timeZoneId]);

  useEffect(() => {
    if (pickerResetKey == null) return;
    setStartDateOverwritten(false);
    setSelectedSlot(undefined);
    setStartDate(DateTime.max(todayStart, minSchedulableDate));
  }, [pickerResetKey, todayStart, minSchedulableDate]);

  useEffect(() => {
    if (
      slots &&
      slots.length > 0 &&
      slots[0].startTime &&
      !startDateOverwritten
    ) {
      setStartDate(
        DateTime.max(
          toStartOfDayInZone(
            toDateTimeInZone(slots[0].startTime, timeZoneId),
            timeZoneId
          ),
          minSchedulableDate
        )
      );
      setStartDateOverwritten(true);
    }
  }, [slots, startDateOverwritten, minSchedulableDate, timeZoneId]);

  return {
    startDate,
    selectedSlot,
    setSelectedSlot,
    incrementDate,
    decrementDate,
  };
};

export { useSiteSurveySlotPickerState };
